/*
 * EW Garage dialog — singleton vehicle + livery picker.
 *
 * Entry points: any element carrying data-open-garage-dialog="{source}".
 * Source determines post-submit routing:
 *   pill / home / series → stay on current page
 *   tune → /pages/configure?chassis={handle}&engine={e}&trim={t}&stage=start (Tune Builder pre-populated)
 *   shop → /pages/{chassis-handle} (chassis hub)
 *
 * BUGS-1 Bug 3 — the dialog used to ship its OWN private fuzzy matcher
 * (a per-candidate scorer + edit-distance/subsequence helpers) and
 * its OWN fetch of assets/ew-chassis-data.json. Both are DELETED. The
 * typeahead now consumes the single canonical suggest/format/resolve API
 * in ew-vehicle-resolver.js (window.EWVehicleResolver) so its suggestions,
 * display, and resolution are IDENTICAL to the homepage input — one
 * resolve function, many surfaces. Writes via EWVehicleResolver.write +
 * window.EWLivery.set. Fires ew:vehicle-changed + ew:livery-changed.
 */
(function () {
  'use strict';

  var dialog = null;
  var lastTrigger = null;
  var source = '';
  var stagedVehicle = null;
  var stagedLivery = null;

  function $(sel, ctx) { return (ctx || document).querySelector(sel); }
  function $$(sel, ctx) { return Array.prototype.slice.call((ctx || document).querySelectorAll(sel)); }

  function resolver() { return window.EWVehicleResolver; }
  /* Shared dataset (loaded ONCE by the resolver module). Always read live so
     a fetch that resolves after the dialog opened is reflected. */
  function chassisRecords() { return (resolver() && resolver().data && resolver().data()) || []; }
  /* Ensure the canonical dataset is loaded; callers chain prefill/decode. */
  function loadChassisData() {
    return (resolver() && resolver().ready) ? resolver().ready() : Promise.resolve();
  }

  function fmtVehicleLabel(item) {
    var parts = [];
    if (item.ys && item.ye) parts.push(item.ys + '–' + item.ye);
    parts.push(item.code);
    if (item.model) parts.push(item.model);
    if (item.engine) {
      var fam = (resolver() && resolver().engineFamily) ? resolver().engineFamily(item.engine) : item.engine;
      parts.push('(' + fam + ')');
    }
    return parts.join(' ');
  }

  /* GAR-1.5 — index of the currently-active (keyboard-highlighted) option,
     or -1 when none. Reset whenever the listbox re-renders or closes. */
  var activeOptionIndex = -1;

  function getCombobox() { return $('[role="combobox"]', dialog); }
  function getOptions() { return $$('.ew-garage-dialog__option', dialog); }

  function setComboExpanded(expanded) {
    var cb = getCombobox();
    if (cb) cb.setAttribute('aria-expanded', expanded ? 'true' : 'false');
  }

  /* Reflect activeOptionIndex onto the DOM: aria-selected on each option,
     aria-activedescendant on the input, .is-active for the visual highlight,
     and scroll the active row into view. */
  function syncActiveOption() {
    var opts = getOptions();
    var input = $('[data-ew-garage-vehicle-input]', dialog);
    var activeId = '';
    for (var i = 0; i < opts.length; i++) {
      var on = i === activeOptionIndex;
      opts[i].setAttribute('aria-selected', on ? 'true' : 'false');
      opts[i].classList.toggle('is-active', on);
      if (on) {
        activeId = opts[i].id;
        if (typeof opts[i].scrollIntoView === 'function') opts[i].scrollIntoView({ block: 'nearest' });
      }
    }
    if (input) {
      if (activeId) input.setAttribute('aria-activedescendant', activeId);
      else input.removeAttribute('aria-activedescendant');
    }
  }

  function moveActiveOption(delta) {
    var opts = getOptions();
    if (!opts.length) return;
    if (activeOptionIndex === -1) {
      activeOptionIndex = delta > 0 ? 0 : opts.length - 1;
    } else {
      activeOptionIndex = (activeOptionIndex + delta + opts.length) % opts.length;
    }
    syncActiveOption();
  }

  /* Close the listbox only (NOT the dialog): hide it, clear active state,
     collapse the combobox. Used by Escape-on-open-listbox and after a pick. */
  function closeListbox() {
    var lb = $('[data-ew-garage-vehicle-listbox]', dialog);
    if (lb) { lb.hidden = true; lb.innerHTML = ''; }
    activeOptionIndex = -1;
    var input = $('[data-ew-garage-vehicle-input]', dialog);
    if (input) input.removeAttribute('aria-activedescendant');
    setComboExpanded(false);
  }

  function listboxIsOpen() {
    var lb = $('[data-ew-garage-vehicle-listbox]', dialog);
    return !!(lb && !lb.hidden && lb.children.length);
  }

  function renderListbox(items) {
    var lb = $('[data-ew-garage-vehicle-listbox]', dialog);
    if (!lb) return;
    activeOptionIndex = -1;
    var input = $('[data-ew-garage-vehicle-input]', dialog);
    if (input) input.removeAttribute('aria-activedescendant');
    if (!items.length) { lb.hidden = true; lb.innerHTML = ''; setComboExpanded(false); return; }
    /* BUGS-1 Bug 3 — model-level rows IDENTICAL to the homepage input:
       line "ys–ye make model · trim", meta "code · engine". Each option
       carries data-code + data-trim + data-engine so selection stages the
       SPECIFIC model/trim (not one row per chassis). Format comes from the
       shared canonical formatter.
       F-05 — data-model added: code+trim is not unique (G20 330i Base vs
       M340i Base), so the pick passes the model for exact resolution. */
    lb.innerHTML = items.map(function (it, idx) {
      var f = resolver().formatSuggestion(it);
      return '<li role="option" id="ew-garage-opt-' + idx + '" tabindex="-1" aria-selected="false"' +
        ' data-code="' + (it.code || '') + '" data-trim="' + (it.trim || '') + '" data-model="' + (it.model || '') + '" data-engine="' + (it.engine || '') + '"' +
        ' class="ew-garage-dialog__option">' +
        '<span class="ew-garage-dialog__option-line">' + f.line + '</span>' +
        '<span class="ew-garage-dialog__option-meta">' + f.meta + '</span>' +
        '</li>';
    }).join('');
    lb.hidden = false;
    setComboExpanded(true);
  }

  /* Shared staging path. BOTH the typeahead/quickpick (selectVehicleByCode)
     and the YMM cascade resolve to ONE dataset record and call this, so the
     shared result card reads identically regardless of on-ramp.
     F-05 — `overrides.year` is the ONLY way a model year enters the staged
     vehicle, and only an on-ramp that captured a genuine user year (typed
     query parsed via EWVehicleResolver.yearFromQuery, validated inside the
     row's ys–ye band) supplies it. No on-ramp year → year stays UNKNOWN:
     the vehicle carries the honest ys/ye band and the label renders the
     band form. The band end is NEVER styled as a model year. */
  function stageVehicle(found, overrides) {
    overrides = overrides || {};
    if (!found) return;

    var pickedTrim = overrides.trim || '';
    stagedVehicle = {
      code:    found.code,
      engine:  found.engine || '',
      make:    found.make  || '',
      model:   found.model || '',
      trim:    pickedTrim,
      ys:      found.ys || '',
      ye:      found.ye || '',
      source:  source || 'garage-dialog',
    };
    if (typeof overrides.year === 'number' && isFinite(overrides.year)) {
      stagedVehicle.year = overrides.year;
      stagedVehicle.year_known = true; /* provenance: user-supplied, band-validated */
    }
    /* Canonical label via the shared formatter (year-known → "2021 BMW 330i
       (G20 · B48)"; unknown → "2019–2025 G20 330i"). fmtVehicleLabel is the
       resolver-absent fallback — it is already the honest band form. */
    stagedVehicle.label = (resolver() && typeof resolver().displayLabel === 'function')
      ? resolver().displayLabel(stagedVehicle)
      : fmtVehicleLabel(found);

    var sel = $('[data-ew-garage-vehicle-selected]', dialog);
    var label = $('[data-ew-garage-vehicle-selected-label]', dialog);
    var meta = $('[data-ew-garage-vehicle-selected-meta]', dialog);
    if (label) label.textContent = fmtVehicleLabel(found);
    if (meta) {
      /* GATE1-1 F-12 — the YOUR CAR meta shows the engine FAMILY ("B48"),
         never the full variant code ("B48B20O1") that 43/249 dataset rows
         carry. Display-time only: stagedVehicle.engine keeps the full code
         (matching/tune resolution depends on it). The full code stays one
         hover away via title= when it differs from the family. */
      var engRaw = found.engine || '';
      var engFam = (engRaw && resolver() && resolver().engineFamily)
        ? resolver().engineFamily(engRaw)
        : engRaw;
      meta.textContent = engFam || (stagedVehicle.year ? String(stagedVehicle.year) : '');
      if (engRaw && engFam && engRaw !== engFam) meta.setAttribute('title', engRaw);
      else meta.removeAttribute('title');
    }
    if (sel) sel.hidden = false;

    var input = $('[data-ew-garage-vehicle-input]', dialog);
    if (input) input.value = '';
    closeListbox();
    var noMatch = $('[data-ew-garage-no-match]', dialog);
    if (noMatch) { noMatch.hidden = true; noMatch.classList.remove('is-prompt'); }

    updateSaveState();
  }

  /* Quickpick / VIN / prefill on-ramp: code only, no specific trim. Resolves
     the first dataset record for the code via the shared module. Trim stays
     empty (eyebrow reads "[F80] BMW M3"). */
  function selectVehicleByCode(code) {
    if (!code) return;
    var found = resolver().resolveByCode(code) || { code: String(code).toUpperCase() };
    stageVehicle(found, {});
  }

  /* BUGS-1 Bug 3 — typeahead pick on-ramp: stage the SPECIFIC model/trim the
     visitor chose from the suggest() listbox, so "YOUR CAR" reflects that
     trim (e.g. "2021–2026 G80 M3 · Competition (S58B30T0)"). Resolves the
     exact code+trim record from the shared dataset and passes trim through.
     F-05 — both pick paths (option click AND the Enter key) land here, so
     the typed query is captured HERE, before stageVehicle clears the input:
     a model year typed in the query ("2021 330i"), validated inside the
     matched row's ys–ye band, becomes the vehicle's provenance year. */
  function selectVehicleByCodeTrim(code, trim, model) {
    if (!code) return;
    var found = resolver().resolveByCodeTrim(code, trim, model) || { code: String(code).toUpperCase() };
    var input = $('[data-ew-garage-vehicle-input]', dialog);
    var typedYear = (resolver() && typeof resolver().yearFromQuery === 'function')
      ? resolver().yearFromQuery(input ? input.value : '', found)
      : null;
    stageVehicle(found, { trim: trim || '', year: typedYear });
  }

  function clearVehicle() {
    stagedVehicle = null;
    var sel = $('[data-ew-garage-vehicle-selected]', dialog);
    if (sel) sel.hidden = true;
    var meta = $('[data-ew-garage-vehicle-selected-meta]', dialog);
    if (meta) meta.textContent = '';
    resetVin();
    updateSaveState();
  }

  /* CHASSIS-NAV-1 Phase 3b — stage a pick emitted by the unified
     ew-chassis-picker (onselect="emit"). The picker dispatches ew:cp-emit with
     { chassis, entry, vehicle, engine_family }; it does NOT persist/navigate/
     fire the global ew:vehicle-resolved, so the pick lands here and flows
     through the SAME stageVehicle → shared result card → Save/per-context-route
     path as the typeahead, quickpicks, and VIN. Map the picker's resolved shape
     onto the dialog's dataset-record shape (engine FROM engine_family so the
     resolver/eyebrow read the normalized family, identical resolution). */
  function stageFromPicker(detail) {
    if (!detail) return;
    var chassis = detail.chassis || {};
    var entry = detail.entry || {};
    var found = {
      code:   chassis.code || (detail.vehicle && detail.vehicle.code) || '',
      engine: detail.engine_family || entry.engine_family || entry.engine_code || '',
      make:   entry.make || chassis.make || '',
      model:  entry.model || '',
      ys:     entry.ys || chassis.ys || '',
      ye:     entry.ye || chassis.ye || '',
    };
    if (!found.code) return;
    /* F-05 — the picker emit detail carries only the ys/ye BAND (verified in
       ew-chassis-picker.js: entry/chassis have no exact-year field), so a
       browse pick never supplies overrides.year — year stays UNKNOWN. */
    stageVehicle(found, { trim: entry.trim || '' });
  }

  /* ---- Secondary method on-ramps (GAR-1.2: YMM only) -------------------
     Each alt-btn is an equivalent entry to ONE task. Opening a method
     reveals the recessed method-panel + that pane; clicking the active
     method again collapses back to typeahead-only. Only ONE pane is ever
     visible — forward-compatible for chassis (1.3) + VIN (1.4). */
  var activeMethod = null;

  function setMethod(method) {
    var panel = $('[data-ew-garage-method-panel]', dialog);
    if (!panel) return;
    /* Toggle off if re-clicking the open method. */
    if (activeMethod === method) { method = null; }
    activeMethod = method;

    $$('[data-ew-garage-method]', dialog).forEach(function (btn) {
      btn.setAttribute('aria-expanded', btn.getAttribute('data-ew-garage-method') === method ? 'true' : 'false');
    });
    $$('[data-ew-garage-method-pane]', dialog).forEach(function (pane) {
      pane.hidden = pane.getAttribute('data-ew-garage-method-pane') !== method;
    });
    panel.hidden = !method;
    /* CHASSIS-NAV-1 Phase 3b — 'browse' reveals the unified ew-chassis-picker,
       which loads its own data + self-initializes; no seeding here. VIN needs
       no seeding either. */
  }

  /* CHASSIS-NAV-1 Phase 3b — the YMM cascade + chassis-code/engine selects
     that previously lived here were REPLACED by the unified ew-chassis-picker
     rendered in the 'browse' pane (compact, onselect="emit"). The picker owns
     its own data load + resolution; its pick re-enters via ew:cp-emit →
     stageFromPicker → the SAME shared result card. uniqSorted is retained
     because the VIN ambiguity hand-off (stageDecodedChassis) still uses it. */
  function uniqSorted(arr) {
    var seen = {}, out = [];
    arr.forEach(function (v) { if (v && !seen[v]) { seen[v] = 1; out.push(v); } });
    out.sort();
    return out;
  }

  /* ---- VIN method (GAR-1.4). A fourth equivalent on-ramp: paste a 17-char
     VIN, decode it on-device via the EXISTING static window.ewVinResolver
     (no external/NHTSA API), and route exactly as the standalone
     sections/configure-vin.liquid flow does. The bmw_chassis branch never
     auto-picks an engine: it defers to chassisData truth and, when the
     chassis maps to >1 engine, hands off to the browse pane (unified
     ew-chassis-picker) pre-expanded on that chassis so the visitor confirms
     the model/engine there. */
  var VIN_STATUS_DEFAULT = 'Decoded from your VIN on this page — optional, never required.';

  function setVinStatus(msg, isError) {
    var el = $('[data-ew-garage-vin-status]', dialog);
    if (!el) return;
    el.textContent = (isError ? '! ' : '') + (msg || VIN_STATUS_DEFAULT);
    el.classList.toggle('is-error', !!isError);
  }

  function resetVin() {
    var input = $('[data-ew-garage-vin]', dialog);
    if (input) input.value = '';
    setVinStatus('', false);
  }

  function decodeVin() {
    var input = $('[data-ew-garage-vin]', dialog);
    if (!input) return;
    var vin = (input.value || '').trim().toUpperCase();
    if (!(window.ewVinResolver && typeof window.ewVinResolver.resolve === 'function')) {
      setVinStatus('VIN decoder is still loading — try again in a moment.', true);
      return;
    }
    var res = window.ewVinResolver.resolve(vin);
    switch (res.kind) {
      case 'invalid':
        setVinStatus('That doesn’t look like a 17-character VIN.', true);
        return;
      case 'other_brand':
        window.location.href = '/pages/configure-other-brand?brand=' +
          encodeURIComponent(res.brand) + '&vin=' + encodeURIComponent(res.vin);
        return;
      case 'unknown':
        window.location.href = '/pages/configure-unknown-vehicle?vin=' +
          encodeURIComponent(res.vin);
        return;
      case 'bmw_unknown':
        setVinStatus('That’s a BMW we couldn’t pin from the VIN. Try Year/Make/Model or chassis code above.', true);
        return;
      case 'bmw_chassis':
        stageDecodedChassis(res);
        return;
      default:
        window.location.href = '/pages/configure-unknown-vehicle?vin=' +
          encodeURIComponent(res.vin || vin);
        return;
    }
  }

  /* Resolve a decoded BMW chassis against chassisData (the dataset of
     record truth), NOT the decoder's coarse single-engine guess. If the
     chassis maps to exactly one engine in chassisData → stage directly.
     If it maps to >1 engine → never guess: hand off to the unified
     ew-chassis-picker (browse pane) pre-expanded on that chassis so the
     visitor confirms the engine/model there. The picker's pick re-enters via
     ew:cp-emit → stageFromPicker. (CHASSIS-NAV-1 Phase 3b — replaces the prior
     hand-off to the now-removed chassis-code select pane.) */
  function stageDecodedChassis(res) {
    var run = function () {
      var code = res.code || res.chassis;
      var matches = chassisRecords().filter(function (c) { return (c.code || '').toUpperCase() === String(code).toUpperCase(); });
      var engines = uniqSorted(matches.map(function (c) { return c.engine; }));

      if (matches.length && engines.length <= 1) {
        /* Single (or no) distinct engine: unambiguous — stage directly into
           the shared result card. */
        var found = matches[0];
        stageVehicle(found, {});
        setVinStatus('', false);
        return;
      }

      if (engines.length > 1) {
        /* Ambiguous — hand off to the browse pane (unified picker) and
           pre-expand that chassis so the visitor confirms the model/engine.
           The picker resolves engine_family and re-enters via ew:cp-emit. */
        setMethodTo('browse');
        expandPickerChassis(code);
        setVinStatus('We found your ' + code + '. Pick the model below.', false);
        return;
      }

      /* Decoder pinned a chassis the dataset doesn't carry — fall back to a
         minimal stage off the decoder's own fields so the pick still works. */
      stageVehicle({ code: code, model: res.model || '', engine: res.engine || '', make: res.make || '' }, {});
      setVinStatus('', false);
    };
    loadChassisData().then(run);
  }

  /* Programmatically expand the unified picker's chip for a given chassis code
     so its model list is shown (used by the VIN ambiguity hand-off). The
     picker's own click delegation handles the expansion; we just click the
     chip once it has rendered. The picker fetches its own data asynchronously,
     so retry briefly until the chip exists. */
  function expandPickerChassis(code) {
    var attempts = 0;
    var tryExpand = function () {
      var picker = $('[data-ew-chassis-picker]', dialog);
      var chip = picker && picker.querySelector('[data-ew-cp-chip="' + String(code).toUpperCase() + '"]');
      if (chip) {
        if (chip.getAttribute('aria-expanded') !== 'true') chip.click();
        return;
      }
      if (attempts++ < 20) setTimeout(tryExpand, 100);
    };
    tryExpand();
  }

  /* Open a specific method pane without the toggle-off behavior of setMethod
     (which collapses if the target is already active). Used by the VIN
     hand-off so the target pane is reliably shown. */
  function setMethodTo(method) {
    var panel = $('[data-ew-garage-method-panel]', dialog);
    if (!panel) return;
    activeMethod = method;
    $$('[data-ew-garage-method]', dialog).forEach(function (btn) {
      btn.setAttribute('aria-expanded', btn.getAttribute('data-ew-garage-method') === method ? 'true' : 'false');
    });
    $$('[data-ew-garage-method-pane]', dialog).forEach(function (pane) {
      pane.hidden = pane.getAttribute('data-ew-garage-method-pane') !== method;
    });
    panel.hidden = !method;
  }

  function clearLivery() {
    stagedLivery = null;
    $$('[data-ew-garage-livery]', dialog).forEach(function (btn) {
      btn.setAttribute('aria-checked', 'false');
      btn.classList.remove('is-selected');
    });
  }

  function selectLivery(name) {
    /* Toggle-off: clicking the already-staged livery deselects it.
       Livery is optional in the unified dialog, so a visitor must be
       able to back out of a pick without committing one. */
    if (stagedLivery === name) {
      clearLivery();
      updateSaveState();
      return;
    }
    stagedLivery = name;
    $$('[data-ew-garage-livery]', dialog).forEach(function (btn) {
      var on = btn.getAttribute('data-ew-garage-livery') === name;
      btn.setAttribute('aria-checked', on ? 'true' : 'false');
      btn.classList.toggle('is-selected', on);
    });
    updateSaveState();
  }

  /* Is there an already-committed selection in the resolver/livery
     stores? Used so Save stays actionable when a returning visitor
     opens the dialog with a saved car/livery and changes nothing. */
  function hasExistingSelection() {
    var v = (window.EWVehicleResolver && window.EWVehicleResolver.read) ? window.EWVehicleResolver.read() : null;
    if (v && (v.code || v.chassis_code || v.chassis)) return true;
    var l = (window.EWLivery && window.EWLivery.read) ? window.EWLivery.read() : (document.body.getAttribute('data-livery') || null);
    return !!l;
  }

  function updateSaveState() {
    var btn = $('[data-ew-garage-save]', dialog);
    var clearAll = $('[data-ew-garage-clear-all]', dialog);
    var hasStaged = !!(stagedVehicle || stagedLivery);
    if (clearAll) clearAll.hidden = !hasStaged;
    if (!btn) return;
    /* Both vehicle and livery are optional. Enable Save whenever
       anything is staged OR an existing committed selection is present.
       When nothing is staged and nothing pre-exists, the button still
       works but reads "Done" — it just closes the dialog. */
    var actionable = hasStaged || hasExistingSelection();
    btn.disabled = false;
    btn.textContent = actionable ? 'Save' : 'Done';
  }

  /* Reveal the gentle "you need a car for this" prompt and keep the
     dialog open. Reuses the existing __no-match hint region so we don't
     introduce a new error surface. */
  function promptForVehicle() {
    var noMatch = $('[data-ew-garage-no-match]', dialog);
    if (noMatch) {
      noMatch.hidden = false;
      noMatch.classList.add('is-prompt');
    }
    var input = $('[data-ew-garage-vehicle-input]', dialog);
    if (input) input.focus();
  }

  /* Persist the staged livery via EWLivery. Do NOT fabricate a livery or
     call setUseCase when livery is null. Prefer setUseCase so the dialog
     writes the SAME store the homepage "What kind of build is it?" cards
     write (use-case + livery together) — otherwise a dialog-set livery
     leaves the use-case unset and the sitewide use-case modal re-prompts.
     The three liveries map 1:1 to the canonical use-case keys. */
  function commitLivery() {
    if (!stagedLivery) return;
    var LIVERY_TO_USECASE = { performance: 'performance', oemplus: 'oemplus', drifter: 'drift' };
    if (window.EWLivery && typeof window.EWLivery.setUseCase === 'function' && LIVERY_TO_USECASE[stagedLivery]) {
      window.EWLivery.setUseCase(LIVERY_TO_USECASE[stagedLivery]); /* persists use-case + sets livery */
    } else if (window.EWLivery && typeof window.EWLivery.set === 'function') {
      window.EWLivery.set(stagedLivery);
    } else {
      document.body.setAttribute('data-livery', stagedLivery);
    }
    document.dispatchEvent(new CustomEvent('ew:livery-changed', { detail: { livery: stagedLivery } }));
  }

  function commitAndRoute() {
    /* Reconcile staged state against what is currently COMMITTED. A
       deselect (staged null where a value was previously committed) must
       persist the clear on Save — not silently keep the old selection. */
    var hadVehicle = !!(window.EWVehicleResolver && window.EWVehicleResolver.read && window.EWVehicleResolver.read());
    var hadLivery = !!(window.EWLivery && window.EWLivery.read && window.EWLivery.read());

    /* True no-op: nothing staged AND nothing committed to clear → this is
       the "Done" path; just close. (Replaces the old blanket early-return
       that silently kept a committed selection after a deselect.) */
    if (!stagedVehicle && !stagedLivery && !hadVehicle && !hadLivery) { closeDialog(); return; }

    /* tune/shop routing needs a chassis. */
    var needsChassis = (source === 'tune' || source === 'shop');
    if (needsChassis && (!stagedVehicle || !(stagedVehicle.code || stagedVehicle.chassis))) {
      /* If the visitor DELIBERATELY cleared the vehicle (had one, staged
         none), commit the clear(s) and just close — do NOT prompt or
         navigate to a broken URL. Only prompt when they're trying to
         proceed but never had/staged a car at all. */
      if (hadVehicle) {
        if (window.EWVehicleResolver && typeof window.EWVehicleResolver.clear === 'function') {
          window.EWVehicleResolver.clear();
        }
        if (!stagedLivery && hadLivery && window.EWLivery && typeof window.EWLivery.clear === 'function') {
          window.EWLivery.clear();
        } else if (stagedLivery) {
          commitLivery();
        }
        closeDialog();
        return;
      }
      promptForVehicle();
      return;
    }

    /* VEHICLE: write if staged; else if a vehicle was committed, the
       visitor deselected it → persist the clear (pill repaints via the
       resolver's ew:vehicle-cleared event). */
    if (stagedVehicle) {
      if (window.EWVehicleResolver && typeof window.EWVehicleResolver.write === 'function') {
        window.EWVehicleResolver.write(stagedVehicle);
      } else {
        try { localStorage.setItem('ew_active_vehicle', JSON.stringify(stagedVehicle)); } catch (e) {}
      }
      document.dispatchEvent(new CustomEvent('ew:vehicle-changed', { detail: stagedVehicle }));
    } else if (hadVehicle && window.EWVehicleResolver && typeof window.EWVehicleResolver.clear === 'function') {
      window.EWVehicleResolver.clear();
    }

    /* LIVERY: write if staged; else if a livery was committed, the visitor
       deselected it → persist the clear. */
    if (stagedLivery) {
      commitLivery();
    } else if (hadLivery && window.EWLivery && typeof window.EWLivery.clear === 'function') {
      window.EWLivery.clear();
    }

    /* Routing by source. Walkthrough Phase 1 Sub-phase F:
       source="home" is DEPRECATED — the homepage uses the inline
       autocomplete widget (snippets/ew-homepage-vehicle-input.liquid),
       not the dialog. Any stray ?source=home triggers fall through
       to the close-stay default behavior. */
    var chassisLc = (stagedVehicle && (stagedVehicle.code || stagedVehicle.chassis)) ? (stagedVehicle.code || stagedVehicle.chassis).toLowerCase() : '';
    if (source === 'tune' && chassisLc) {
      /* The Tune Builder renders on /pages/configure (NOT the homepage). Land
         there carrying the resolved vehicle as URL params — chassis + engine +
         the exact trim the visitor picked — so a shared/bookmarked link resolves
         the same vehicle. (For the in-session handoff the builder also reads the
         persisted vehicle, which already carries the trim; these params keep the
         URL self-describing and let the external deep-link path resolve trim.) */
      var params = 'chassis=' + encodeURIComponent(chassisLc) + '&stage=start';
      var eng = stagedVehicle && (stagedVehicle.engine_code || stagedVehicle.engine);
      if (eng) params += '&engine=' + encodeURIComponent(eng);
      if (stagedVehicle && stagedVehicle.trim) params += '&trim=' + encodeURIComponent(stagedVehicle.trim);
      window.location.href = '/pages/configure?' + params;
      return;
    }
    if (source === 'shop' && chassisLc) {
      window.location.href = '/pages/' + chassisLc;
      return;
    }
    /* pill / series / (deprecated: home) → close, stay on current page */
    closeDialog();
  }

  /* Walkthrough Phase 1 Sub-phase F — per-source dialog title. */
  var SOURCE_TITLES = {
    tune: 'Build Your Tune',
    shop: 'Select Your Vehicle',
    pill: 'My Garage',
    series: 'Set vehicle',
  };

  function openDialog(src, trigger) {
    if (!dialog) return;
    source = src || 'garage-dialog';
    lastTrigger = trigger || null;

    /* Set source-aware title */
    var titleEl = $('[data-ew-garage-dialog-title]', dialog);
    if (titleEl) {
      titleEl.textContent = SOURCE_TITLES[source] || 'Tell us about your car.';
    }

    /* Pre-populate chassis from existing resolver state — must wait
       for chassis data to load so selectVehicleByCode can resolve
       the make/model/engine fields off the JSON. If no resolver
       state, clear immediately (no fetch wait needed). */
    var v = (window.EWVehicleResolver && window.EWVehicleResolver.read) ? window.EWVehicleResolver.read() : null;
    var vCode = v && (v.code || v.chassis_code || v.chassis);
    if (vCode) {
      loadChassisData().then(function () {
        /* F-05 — re-staging the persisted vehicle must not LOSE its
           provenance year or picked trim on a reopen+Save (e.g. a
           livery-only change): carry both through as overrides.
           resolveByCodeTrim falls back to the first record for the code
           when the trim doesn't match, so this supersets the old
           selectVehicleByCode prefill. */
        var ov = { trim: (v && v.trim) || '' };
        if (v && v.year_known === true && typeof v.year === 'number' && isFinite(v.year)) {
          ov.year = v.year;
        }
        var found = resolver().resolveByCodeTrim(vCode, ov.trim, (v && v.model) || '') || { code: String(vCode).toUpperCase() };
        stageVehicle(found, ov);
      });
    } else {
      loadChassisData();
      clearVehicle();
    }

    /* Pre-select the ACTIVE livery so Save is enabled when the visitor
       only changes the vehicle. Was EWLivery.get() — which takes an id
       and, called with no arg, always returned null, so the livery was
       never pre-staged and Save stayed disabled (the "Save didn't take"
       symptom when switching vehicle). EWLivery.read() returns the
       active livery id. */
    var l = (window.EWLivery && window.EWLivery.read) ? window.EWLivery.read() : (document.body.getAttribute('data-livery') || null);
    if (l) selectLivery(l);
    else {
      stagedLivery = null;
      $$('[data-ew-garage-livery]', dialog).forEach(function (btn) {
        btn.setAttribute('aria-checked', 'false');
        btn.classList.remove('is-selected');
      });
    }

    dialog.hidden = false;
    dialog.removeAttribute('inert');
    dialog.classList.add('is-open');
    document.body.style.overflow = 'hidden';

    /* Focus the input */
    setTimeout(function () {
      var input = $('[data-ew-garage-vehicle-input]', dialog);
      if (input) input.focus();
    }, 50);

    document.addEventListener('keydown', onKeydown);
  }

  function closeDialog() {
    if (!dialog) return;
    dialog.classList.remove('is-open');
    dialog.hidden = true;
    dialog.setAttribute('inert', '');
    document.body.style.overflow = '';
    document.removeEventListener('keydown', onKeydown);
    /* Collapse any open secondary method so a reopen starts on the
       typeahead-only view. */
    if (activeMethod) setMethod(activeMethod);
    if (lastTrigger && typeof lastTrigger.focus === 'function') lastTrigger.focus();
    lastTrigger = null;
    source = '';
  }

  /* Visible, tabbable elements inside the dialog, in DOM order. getClientRects()
     filters display:none / hidden-subtree nodes and works for the fixed-position
     panel (where offsetParent is null). Listbox options carry tabindex="-1" so
     they're excluded — they're reached via the arrow-key + aria-activedescendant
     combobox model, not Tab. */
  function getFocusable() {
    return $$('a[href],area[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])', dialog)
      .filter(function (el) { return el.getClientRects().length > 0; });
  }

  function onKeydown(e) {
    if (e.key === 'Escape') { e.preventDefault(); closeDialog(); return; }
    /* Explicit Tab focus-trap: the dialog is aria-modal but the backdrop is not
       inert, so keyboard focus could otherwise tab out behind the modal. Keep
       focus cycling within the dialog (wrap first<->last). */
    if (e.key === 'Tab') {
      var f = getFocusable();
      if (!f.length) return;
      var first = f[0], last = f[f.length - 1];
      var active = document.activeElement;
      if (!dialog.contains(active)) { e.preventDefault(); first.focus(); return; }
      if (e.shiftKey && active === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && active === last) { e.preventDefault(); first.focus(); }
    }
  }

  /* Initial wiring */
  function init() {
    dialog = $('[data-ew-garage-dialog]');
    if (!dialog) return;

    /* Click handler — opens the dialog from any trigger */
    document.addEventListener('click', function (e) {
      var trigger = e.target.closest('[data-open-garage-dialog]');
      if (trigger) {
        e.preventDefault();
        openDialog(trigger.getAttribute('data-open-garage-dialog'), trigger);
        return;
      }
      /* Close handlers */
      if (e.target.closest('[data-ew-garage-dialog-close]') || e.target.matches('[data-ew-garage-dialog-backdrop]')) {
        closeDialog();
        return;
      }
      /* Quick pick */
      var pick = e.target.closest('[data-ew-garage-pick]');
      if (pick) { selectVehicleByCode(pick.getAttribute('data-ew-garage-pick')); return; }
      /* Secondary method on-ramp toggle (GAR-1.2: YMM) */
      var methodBtn = e.target.closest('[data-ew-garage-method]');
      if (methodBtn) { setMethod(methodBtn.getAttribute('data-ew-garage-method')); return; }
      /* Listbox option — model-level suggest() row carries code + trim +
         model (F-05: model disambiguates a code+trim collision). */
      var opt = e.target.closest('.ew-garage-dialog__option');
      if (opt) { selectVehicleByCodeTrim(opt.getAttribute('data-code'), opt.getAttribute('data-trim'), opt.getAttribute('data-model')); return; }
      /* Vehicle clear */
      if (e.target.closest('[data-ew-garage-vehicle-clear]')) { clearVehicle(); return; }
      /* Clear all — DECISIVE immediate wipe. Reset both sections' staged
         state + in-dialog UI (clearVehicle/clearLivery), then ALSO commit
         the clear to persisted state + pill. The resolver/livery clear()
         APIs fire ew:vehicle-cleared + ew:livery-changed{null}, which the
         header repaints the pill from live behind the still-open dialog.
         The dialog stays OPEN in its now-empty state. */
      if (e.target.closest('[data-ew-garage-clear-all]')) {
        clearVehicle();
        clearLivery();
        if (window.EWVehicleResolver && typeof window.EWVehicleResolver.clear === 'function') {
          window.EWVehicleResolver.clear();
        } else {
          /* Fallback: drop the keys directly + fire the event so the pill
             still repaints when the resolver API is unavailable. */
          try {
            localStorage.removeItem('ew_active_vehicle');
            localStorage.removeItem('ew_active_vehicle_ts');
          } catch (e2) {}
          document.dispatchEvent(new CustomEvent('ew:vehicle-cleared'));
        }
        if (window.EWLivery && typeof window.EWLivery.clear === 'function') {
          window.EWLivery.clear();
        } else {
          try {
            localStorage.removeItem('ew_livery');
            localStorage.removeItem('ew_use_case');
            localStorage.removeItem('ew_livery_ts');
          } catch (e3) {}
          document.body.removeAttribute('data-livery');
          document.dispatchEvent(new CustomEvent('ew:livery-changed', { detail: { livery: null } }));
        }
        updateSaveState();
        return;
      }
      /* Livery card */
      var liv = e.target.closest('[data-ew-garage-livery]');
      if (liv) { selectLivery(liv.getAttribute('data-ew-garage-livery')); return; }
      /* VIN decode (GAR-1.4) */
      if (e.target.closest('[data-ew-garage-vin-decode]')) { decodeVin(); return; }
      /* Save */
      if (e.target.closest('[data-ew-garage-save]')) { commitAndRoute(); return; }
    });

    /* Input typing → render listbox */
    var input = $('[data-ew-garage-vehicle-input]', dialog);
    if (input) {
      var debounce;
      /* BUGS-1 Bug 3 — run the SAME canonical matcher the homepage uses
         (EWVehicleResolver.suggest). Ensure the shared dataset is loaded,
         then refire so typing before the fetch lands never silently shows
         nothing permanently. */
      function runSearch(q) {
        if (!q || q.length < 1) {
          renderListbox([]);
          var nm0 = $('[data-ew-garage-no-match]', dialog); if (nm0) nm0.hidden = true;
          return;
        }
        resolver().ready().then(function () {
          if (input.value !== q) return; /* stale — input changed while loading */
          var hits = resolver().suggest(q);
          renderListbox(hits);
          var nm = $('[data-ew-garage-no-match]', dialog);
          if (nm) nm.hidden = hits.length > 0;
        });
      }
      input.addEventListener('input', function () {
        clearTimeout(debounce);
        debounce = setTimeout(function () { runSearch(input.value); }, 80);
      });
      /* GAR-1.5 — full keyboard operation of the combobox listbox. */
      input.addEventListener('keydown', function (e) {
        var open = listboxIsOpen();
        switch (e.key) {
          case 'ArrowDown':
            if (open) { e.preventDefault(); moveActiveOption(1); }
            return;
          case 'ArrowUp':
            if (open) { e.preventDefault(); moveActiveOption(-1); }
            return;
          case 'Home':
            if (open) { e.preventDefault(); activeOptionIndex = 0; syncActiveOption(); }
            return;
          case 'End':
            if (open) { e.preventDefault(); var n = getOptions().length; activeOptionIndex = n - 1; syncActiveOption(); }
            return;
          case 'Enter': {
            if (!open) return;
            e.preventDefault();
            var opts = getOptions();
            /* Select the ACTIVE option; if none active, fall back to the
               top-ranked (first) row. */
            var target = activeOptionIndex >= 0 ? opts[activeOptionIndex] : opts[0];
            if (target) selectVehicleByCodeTrim(target.getAttribute('data-code'), target.getAttribute('data-trim'), target.getAttribute('data-model'));
            return;
          }
          case 'Escape':
            /* If the listbox is open, close ONLY the listbox and stop the
               event before the dialog-level Escape handler can close the
               whole dialog. If it's closed, let onKeydown close the dialog. */
            if (open) {
              e.preventDefault();
              e.stopPropagation();
              closeListbox();
            }
            return;
          default:
            return;
        }
      });
    }

    /* GAR-1.5 — mouse hover syncs the keyboard "active" highlight so
       pointer + keyboard share one active row (no dual highlight). */
    var listbox = $('[data-ew-garage-vehicle-listbox]', dialog);
    if (listbox) {
      listbox.addEventListener('mousemove', function (e) {
        var opt = e.target.closest('.ew-garage-dialog__option');
        if (!opt) return;
        var opts = getOptions();
        var idx = opts.indexOf(opt);
        if (idx !== -1 && idx !== activeOptionIndex) { activeOptionIndex = idx; syncActiveOption(); }
      });
    }

    /* CHASSIS-NAV-1 Phase 3b — the unified ew-chassis-picker (browse pane,
       onselect="emit") dispatches ew:cp-emit on its root when a model resolves.
       Listen on the dialog so the pick stages into the SAME shared result card
       + Save/per-context-route flow as the typeahead/quickpicks/VIN. The
       picker neither persists nor navigates in emit mode, so the dialog stays
       open and Save still owns the commit + routing. */
    dialog.addEventListener('ew:cp-emit', function (e) {
      stageFromPicker(e.detail);
    });

    /* VIN method — normalize input (uppercase, strip invalid chars) and
       decode on Enter; the Decode button click is handled by delegation. */
    var vinInput = $('[data-ew-garage-vin]', dialog);
    if (vinInput) {
      vinInput.addEventListener('input', function () {
        var raw = vinInput.value.toUpperCase().replace(/[\s-]/g, '').replace(/[^A-HJ-NPR-Z0-9]/g, '');
        if (raw !== vinInput.value) vinInput.value = raw;
      });
      vinInput.addEventListener('keydown', function (e) {
        if (e.key === 'Enter') { e.preventDefault(); decodeVin(); }
      });
    }
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
